import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BrowserProvider, Contract, parseUnits, isAddress } from "ethers";
import { getToken } from "@/lib/api";
import { getMyTokens, setTokenTracked } from "@/lib/myTokens";
import { useWallet } from "@/contexts/WalletContext";
import { HoldersTable } from "@/components/explorer/TokenOverview";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Coins, Send, Loader2, Plus, Trash2, Hammer } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { FADE_UP } from "@/lib/motion";
import { TokenLogo } from "@/components/common/TokenLogo";
import { LogoUploadButton } from "@/components/common/LogoUpload";
import { PortfolioCard } from "@/components/wallet/Portfolio";

const fmt = (n) => Number(n).toLocaleString(undefined, { maximumFractionDigits: 4 });
const ERC20 = ["function transfer(address to, uint256 amount) returns (bool)"];

const SendForm = ({ token, onSent }) => {
  const { getProvider } = useWallet();
  const [to, setTo] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const id = token.symbol.toLowerCase();

  const send = async (e) => {
    e.preventDefault();
    if (!isAddress(to)) return toast.error("Enter a valid recipient address");
    if (!(Number(amount) > 0)) return toast.error("Enter an amount greater than 0");
    setBusy(true);
    try {
      const signer = await new BrowserProvider(getProvider()).getSigner();
      const tx = await new Contract(token.address, ERC20, signer).transfer(to, parseUnits(amount, token.decimals));
      toast.info("Transfer submitted, waiting for confirmation…");
      await tx.wait();
      toast.success(`Sent ${amount} ${token.symbol}`);
      setAmount("");
      onSent(tx.hash);
    } catch (err) {
      const rejected = err?.code === "ACTION_REJECTED" || err?.info?.error?.code === 4001;
      toast.error(rejected ? "Transfer rejected in your wallet" : /exceeds balance|insufficient/i.test(err?.message || "") ? "Insufficient balance" : "Transfer failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={send} data-testid={`send-form-${id}`} className="grid grid-cols-1 sm:grid-cols-[1fr_140px_auto] gap-2">
      <Input data-testid={`send-to-${id}`} placeholder="Recipient 0x…" value={to} onChange={(e) => setTo(e.target.value.trim())}
        className="bg-[#050505] border-[#2A2411] text-white font-mono text-xs placeholder:text-zinc-600" />
      <Input data-testid={`send-amount-${id}`} placeholder="Amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)}
        className="bg-[#050505] border-[#2A2411] text-white placeholder:text-zinc-600" />
      <Button data-testid={`send-btn-${id}`} type="submit" disabled={busy} className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB] font-semibold">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <><Send size={14} className="mr-1.5" /> Send</>}
      </Button>
    </form>
  );
};

const TokenCard = ({ entry, owner, onRemove }) => {
  const queryClient = useQueryClient();
  const { data, isError } = useQuery({ queryKey: ["token", entry.address, owner], queryFn: () => getToken(entry.address, owner), refetchInterval: 10000 });
  const symbol = data?.symbol || entry.symbol || "token";
  const id = symbol.toLowerCase();
  const [logoVersion, setLogoVersion] = useState(null);

  return (
    <Card data-testid={`my-token-${id}`} className="border-[#2A2411] bg-[#0A0A0A]">
      <CardContent className="p-6 space-y-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <TokenLogo key={logoVersion} address={entry.address} symbol={symbol} size="lg" version={logoVersion} testId={`my-token-logo-${id}`} />
            <div className="min-w-0">
              <p className="font-display text-xl font-bold text-white">{data?.name || entry.name} <span className="text-[#D4AF37]">{symbol}</span></p>
              <Link to={`/address/${entry.address}`} className="table-link font-mono text-xs break-all">{entry.address}</Link>
              {!onRemove && <div className="mt-1"><LogoUploadButton tokenAddress={entry.address} symbol={symbol} onUploaded={() => setLogoVersion(Date.now())} /></div>}
            </div>
          </div>
          {onRemove ? (
            <button data-testid={`remove-token-${id}`} onClick={() => onRemove(entry.address)} title="Stop tracking" className="text-zinc-600 hover:text-red-400 transition-colors">
              <Trash2 size={16} />
            </button>
          ) : (
            <span data-testid={`deployed-badge-${id}`} className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-[#D4AF37]/10 text-[#D4AF37] shrink-0">Deployed by you</span>
          )}
        </div>
        {isError && <p className="text-sm text-red-400">Could not load this token from the chain.</p>}
        {data && (
          <>
            <div className="grid grid-cols-3 gap-4">
              <div><p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Your balance</p><p data-testid={`my-balance-${id}`} className="font-display font-bold text-white">{fmt(data.holderBalance || 0)}</p></div>
              <div><p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Total supply</p><p className="font-display font-bold text-white">{fmt(data.totalSupply)}</p></div>
              <div><p className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Holders</p><p data-testid={`holder-count-${id}`} className="font-display font-bold text-white">{data.holderCount}</p></div>
            </div>
            <SendForm token={data} onSent={() => setTimeout(() => queryClient.invalidateQueries({ queryKey: ["token", entry.address, owner] }), 500)} />
            <HoldersTable holders={data.holders.slice(0, 10)} symbol={data.symbol} highlight={owner} />
          </>
        )}
      </CardContent>
    </Card>
  );
};

const signError = (err, fallback) =>
  err?.code === "ACTION_REJECTED" || err?.info?.error?.code === 4001 ? "Signature was rejected in your wallet" : err?.response?.data?.detail || fallback;

const useTrackedTokens = (address) => {
  const { getProvider } = useWallet();
  const queryClient = useQueryClient();
  const queryKey = ["myTokens", address?.toLowerCase()];
  const query = useQuery({ queryKey, queryFn: () => getMyTokens(address), enabled: Boolean(address), refetchInterval: 15000 });
  const change = async (tokenAddress, action) => {
    const res = await setTokenTracked({ eip1193: getProvider(), owner: address, address: tokenAddress, action });
    await queryClient.invalidateQueries({ queryKey });
    return res;
  };
  return { ...query, change };
};

export default function MyTokensPage() {
  const { address } = useWallet();
  const { data, isLoading, change } = useTrackedTokens(address);
  const [importAddr, setImportAddr] = useState("");
  const [importing, setImporting] = useState(false);
  const deployed = data?.deployed || [];
  const tracked = data?.tracked || [];

  const importToken = async (e) => {
    e.preventDefault();
    if (!isAddress(importAddr)) return toast.error("Enter a valid token contract address");
    setImporting(true);
    try {
      await getToken(importAddr);
      const res = await change(importAddr, "track");
      setImportAddr("");
      toast.success(`Tracking ${res.token.symbol}`);
    } catch (err) {
      toast.error(signError(err, "Not an ERC-20 token on FSC"));
    } finally {
      setImporting(false);
    }
  };

  const untrack = (tokenAddress) =>
    change(tokenAddress, "untrack").then(() => toast.success("Stopped tracking token")).catch((err) => toast.error(signError(err, "Could not update tracked tokens")));

  return (
    <div data-testid="my-tokens-page" className="max-w-4xl mx-auto px-4 sm:px-6 py-10 space-y-6">
      <motion.div {...FADE_UP}>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-2">My <span className="text-[#D4AF37]">Tokens</span></h1>
        <p className="text-zinc-400">Tokens you've deployed on FSC, with live holder balances and quick sends.</p>
      </motion.div>

      {!address ? (
        <Card className="border-[#2A2411] bg-[#0A0A0A]"><CardContent data-testid="my-tokens-connect" className="p-8 text-center text-zinc-400">Connect your wallet to see your tokens.</CardContent></Card>
      ) : (
        <>
          <PortfolioCard address={address} />
          <form onSubmit={importToken} className="flex gap-2">
            <Input data-testid="import-token-input" placeholder="Track another token by contract address (0x…)" value={importAddr}
              onChange={(e) => setImportAddr(e.target.value.trim())} className="bg-[#0A0A0A] border-[#2A2411] text-white font-mono text-xs placeholder:text-zinc-600" />
            <Button data-testid="import-token-btn" type="submit" disabled={importing} variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 shrink-0">
              {importing ? <Loader2 size={14} className="animate-spin" /> : <><Plus size={14} className="mr-1" /> Track</>}
            </Button>
          </form>
          {isLoading ? (
            <div data-testid="my-tokens-loading" className="h-32 rounded-lg bg-[#0A0A0A] border border-[#2A2411] animate-pulse" />
          ) : deployed.length + tracked.length === 0 ? (
            <Card className="border-[#2A2411] bg-[#0A0A0A]">
              <CardContent data-testid="my-tokens-empty" className="p-8 text-center space-y-3">
                <Coins size={28} className="mx-auto text-[#D4AF37]" />
                <p className="text-zinc-400">You haven't deployed any tokens from this wallet yet.</p>
                <Link to="/get-started" className="inline-flex items-center gap-2 text-sm font-semibold text-[#D4AF37] hover:text-[#F3E5AB]"><Hammer size={14} /> Deploy your first token</Link>
              </CardContent>
            </Card>
          ) : (
            <>
              {deployed.map((t) => <TokenCard key={t.address} entry={t} owner={address} />)}
              {tracked.map((t) => <TokenCard key={t.address} entry={t} owner={address} onRemove={untrack} />)}
            </>
          )}
        </>
      )}
    </div>
  );
}
